import { renderHeightCm } from "./scale-sheet-height.ts"

export const CANVAS_WIDTH = 2048
export const CANVAS_HEIGHT = 1152
export const ASPECT_RATIO = 16 / 9

export const SCALE_POSE_ASPECT = 1
export const DEFAULT_SCALE_POSE_WIDTH = 1024
export const DEFAULT_SCALE_POSE_HEIGHT = 1024

export const PADDING_X = 48
export const PADDING_TOP = Math.ceil(CANVAS_HEIGHT * 0.15)
export const PADDING_BOTTOM = 96
export const GAP_X = 16
export const MIN_PX_PER_CM = 2.0
export const PREVIEW_PADDING_TOP = 50
export const PREVIEW_PADDING_BOTTOM = 50
export const PREVIEW_LABEL_RESERVE = 48
export const SCALE_SHEET_EDIT_FRAME_PADDING_Y_PX = 64
export const SINGLE_CHARACTER_PREVIEW_FRAME_HEIGHT_PX = 480
export const SCALE_SHEET_SERVER_PREVIEW_MAX_PX = 800
/** Matches backend Layout::BACKGROUND_RGB / SCALE_POSE_BG_RGB */
export const SCALE_SHEET_CANVAS_BG = "#C0C0C0"

export type ScaleSheetLayoutMode = "preview" | "export"

export type ScaleSheetEntityType = "character" | "prop"

export type ScaleSheetFigureInput = {
  character_id: number
  /** Defaults to "character". Required for mixed sheets to avoid id collisions. */
  entity_type?: ScaleSheetEntityType
  name: string
  height_cm: number
  image_width: number
  image_height: number
  scale_pose_url?: string | null
}

export type ScaleSheetPlacedFigure = {
  character_id: number
  entity_type: ScaleSheetEntityType
  name: string
  height_cm: number
  x: number
  y: number
  w: number
  h: number
  baseline_y: number
  hit_rect: { x: number; y: number; w: number; h: number }
  label: string
  scale_pose_url?: string | null
}

export function scaleSheetEntityKey(entityType: ScaleSheetEntityType, id: number): string {
  return entityType === "prop" ? `p:${id}` : `c:${id}`
}

export function scaleSheetFigureEntityKey(fig: { character_id: number; entity_type?: ScaleSheetEntityType }): string {
  return scaleSheetEntityKey(fig.entity_type ?? "character", fig.character_id)
}

export function parseScaleSheetEntityKey(key: string): { entity_type: ScaleSheetEntityType; id: number } | null {
  const match = /^(c|p):(\d+)$/.exec(key)
  if (!match) return null
  return {
    entity_type: match[1] === "p" ? "prop" : "character",
    id: Number(match[2]),
  }
}

export type ScaleSheetLayoutResult = {
  px_per_cm: number
  px_per_cm_preview: number
  export_clamped: boolean
  content_width_preview: number
  figures: ScaleSheetPlacedFigure[]
  canvas: { w: number; h: number }
  preview_display_zone?: { top: number; bottom: number }
}

export type ScaleSheetLayoutOptions = {
  referenceMaxHeightCm?: number
}

function layoutMetrics(mode: ScaleSheetLayoutMode) {
  if (mode === "preview") {
    const baseline = CANVAS_HEIGHT - PREVIEW_PADDING_BOTTOM - PREVIEW_LABEL_RESERVE
    return {
      paddingTop: PREVIEW_PADDING_TOP,
      baselineY: baseline,
      figuresZoneHeight: baseline - PREVIEW_PADDING_TOP,
    }
  }
  return {
    paddingTop: PADDING_TOP,
    baselineY: CANVAS_HEIGHT - PADDING_BOTTOM,
    figuresZoneHeight: CANVAS_HEIGHT - PADDING_TOP - PADDING_BOTTOM,
  }
}

export function formatLabelCm(cm: number) {
  const value = Math.round(cm * 10) / 10
  const text = value % 1 === 0 ? String(value) : String(value)
  return `${text} cm`
}

function widthCmEquivalent(fig: ScaleSheetFigureInput) {
  if (fig.image_height <= 0) return 0
  return renderHeightCm(fig.height_cm) * (fig.image_width / fig.image_height)
}

function scaleFor(fig: ScaleSheetFigureInput, pxPerCm: number) {
  if (fig.image_height <= 0) return 1
  return (renderHeightCm(fig.height_cm) * pxPerCm) / fig.image_height
}

function pxPerCmByHeight(sorted: ScaleSheetFigureInput[], mode: ScaleSheetLayoutMode, referenceMaxHeightCm?: number) {
  const maxFromFigures = Math.max(...sorted.map((f) => renderHeightCm(f.height_cm)), 0)
  const maxH =
    mode === "preview" && referenceMaxHeightCm != null && referenceMaxHeightCm > 0
      ? renderHeightCm(referenceMaxHeightCm)
      : maxFromFigures
  if (maxH <= 0) return MIN_PX_PER_CM
  return layoutMetrics(mode).figuresZoneHeight / maxH
}

function pxPerCmByWidth(sorted: ScaleSheetFigureInput[]) {
  const available = CANVAS_WIDTH - PADDING_X * 2 - GAP_X * Math.max(sorted.length - 1, 0)
  const widthEquiv = sorted.reduce((sum, f) => sum + widthCmEquivalent(f), 0)
  if (widthEquiv <= 0) return MIN_PX_PER_CM
  return available / widthEquiv
}

function placeFigures(
  sorted: ScaleSheetFigureInput[],
  pxPerCm: number,
  mode: ScaleSheetLayoutMode,
): ScaleSheetPlacedFigure[] {
  let nextX = PADDING_X
  const baseY = layoutMetrics(mode).baselineY
  const placed = sorted.map((fig) => {
    const scale = scaleFor(fig, pxPerCm)
    const w = Math.round(fig.image_width * scale)
    const h = Math.round(fig.image_height * scale)
    const x = nextX
    const y = Math.round(baseY - h)
    const figure: ScaleSheetPlacedFigure = {
      character_id: fig.character_id,
      entity_type: fig.entity_type ?? "character",
      name: fig.name,
      height_cm: fig.height_cm,
      x,
      y,
      w,
      h,
      baseline_y: baseY,
      hit_rect: { x, y, w, h: h + 40 },
      label: `${fig.name} — ${formatLabelCm(fig.height_cm)}`,
      scale_pose_url: fig.scale_pose_url,
    }
    nextX = x + w + GAP_X
    return figure
  })
  return centerRow(placed)
}

/**
 * Horizontally center the figure row on the canvas; feet stay on baseline.
 * Skip when the row overflows the canvas (preview height-priority) — negative x
 * would clip the start of the scrollable modal.
 */
function centerRow(placed: ScaleSheetPlacedFigure[]): ScaleSheetPlacedFigure[] {
  if (placed.length === 0) return placed
  const first = placed[0]
  const last = placed[placed.length - 1]
  const rowW = last.x + last.w - first.x
  if (rowW > CANVAS_WIDTH) return placed
  const offset = Math.round((CANVAS_WIDTH - rowW) / 2)
  if (offset === first.x) return placed
  const dx = offset - first.x
  return placed.map((fig) => ({
    ...fig,
    x: fig.x + dx,
    hit_rect: { ...fig.hit_rect, x: fig.hit_rect.x + dx },
  }))
}

function rowContentWidth(placed: ScaleSheetPlacedFigure[]) {
  if (placed.length === 0) return CANVAS_WIDTH
  const last = placed[placed.length - 1]
  return last.x + last.w + PADDING_X
}

function previewDisplayZoneFrom(placed: ScaleSheetPlacedFigure[]): { top: number; bottom: number } | undefined {
  if (placed.length === 0) return undefined
  if (placed.length === 1) {
    const fig = placed[0]
    return {
      top: fig.y - PREVIEW_PADDING_TOP,
      bottom: fig.baseline_y + PREVIEW_LABEL_RESERVE,
    }
  }
  return {
    top: PREVIEW_PADDING_TOP,
    bottom: placed[0].baseline_y + PREVIEW_LABEL_RESERVE,
  }
}

function emptyResult(): ScaleSheetLayoutResult {
  return {
    px_per_cm: MIN_PX_PER_CM,
    px_per_cm_preview: MIN_PX_PER_CM,
    export_clamped: false,
    content_width_preview: CANVAS_WIDTH,
    figures: [],
    canvas: { w: CANVAS_WIDTH, h: CANVAS_HEIGHT },
  }
}

export function computeLayout(
  figures: ScaleSheetFigureInput[],
  mode: ScaleSheetLayoutMode = "export",
  options?: ScaleSheetLayoutOptions,
): ScaleSheetLayoutResult {
  if (figures.length === 0) return emptyResult()

  const ordered = [...figures]
  const referenceMaxHeightCm = options?.referenceMaxHeightCm
  const pxByHeightExport = pxPerCmByHeight(ordered, "export")
  const pxByHeightPreview = pxPerCmByHeight(ordered, "preview", referenceMaxHeightCm)
  const pxByWidth = pxPerCmByWidth(ordered)
  let pxExport = Math.min(pxByHeightExport, pxByWidth)

  let exportClamped = false
  if (pxExport < MIN_PX_PER_CM) {
    exportClamped = true
    pxExport = Math.max(0.01, Math.min(pxByWidth, pxExport))
  }

  const pxPreview = pxByHeightPreview

  const placedPreview = placeFigures(ordered, pxPreview, "preview")
  const contentWidthPreview = ordered.length === 1 && mode === "preview" ? CANVAS_WIDTH : rowContentWidth(placedPreview)
  const placedExport = placeFigures(ordered, pxExport, "export")
  const previewDisplayZone = previewDisplayZoneFrom(placedPreview)

  return {
    px_per_cm: pxExport,
    px_per_cm_preview: pxPreview,
    export_clamped: exportClamped,
    content_width_preview: contentWidthPreview,
    figures: mode === "preview" ? placedPreview : placedExport,
    canvas: { w: CANVAS_WIDTH, h: CANVAS_HEIGHT },
    preview_display_zone: previewDisplayZone,
  }
}

/** Apply draft height to figure labels without changing layout geometry (single-hero edit). */
export function previewFiguresWithDraftHeight(
  figures: ScaleSheetPlacedFigure[],
  heightCm: number,
  name: string,
): ScaleSheetPlacedFigure[] {
  return figures.map((fig) => ({
    ...fig,
    height_cm: heightCm,
    label: `${name} — ${formatLabelCm(heightCm)}`,
  }))
}

/** Build figure inputs from server rows + draft heights keyed by entity key (`c:1` / `p:3`). */
export function buildFigureInputsFromCharacters(
  characters: {
    id: number
    name: string
    scale_pose_url?: string | null
    image_width?: number
    image_height?: number
    entity_type?: ScaleSheetEntityType
  }[],
  heightsCm: Record<string, number>,
  defaultEntityType: ScaleSheetEntityType = "character",
): ScaleSheetFigureInput[] {
  return characters.map((c) => {
    const entityType = c.entity_type ?? defaultEntityType
    const key = scaleSheetEntityKey(entityType, c.id)
    return {
      character_id: c.id,
      entity_type: entityType,
      name: c.name,
      height_cm: heightsCm[key] ?? 180,
      image_width: c.image_width ?? DEFAULT_SCALE_POSE_WIDTH,
      image_height: c.image_height ?? DEFAULT_SCALE_POSE_HEIGHT,
      scale_pose_url: c.scale_pose_url,
    }
  })
}
